import { inject, Service } from '@angular/core';
import type { HubConnection, IHttpConnectionOptions } from '@microsoft/signalr';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { HUB_RECONNECT_DELAYS_MS, HUBS_PATH_PREFIX } from './realtime.config';

// Mirrors the backend `SignalR:Enabled` flag. While off, no hub endpoint exists and `create` returns
// nothing. The client is imported dynamically so those installs never load it.
@Service()
export class HubConnectionFactory {
  private readonly config = inject(RuntimeConfigService);

  enabled(): boolean {
    return this.config.get('signalR')?.enabled ?? false;
  }

  hubUrl(hubName: string): string {
    const base = (this.config.get('backendBaseUrl') ?? '').replace(/\/+$/, '');
    return `${base}${HUBS_PATH_PREFIX}/${hubName}`;
  }

  // Browser WebSockets cannot send `Authorization`. The handshake authenticates from the `jwt` cookie,
  // so no token reaches the URL or the edge access logs.
  connectionOptions(): IHttpConnectionOptions {
    return {
      withCredentials: true,
    };
  }

  async create(hubName: string): Promise<HubConnection | null> {
    if (!this.enabled()) {
      return null;
    }

    const signalR = await import('@microsoft/signalr');

    return new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl(hubName), this.connectionOptions())
      .withAutomaticReconnect([...HUB_RECONNECT_DELAYS_MS])
      .configureLogging(signalR.LogLevel.Warning)
      .build();
  }
}
